import type { AccessMode, CEFRLevel, Language, Marathon, RatingScope } from '@/api/courses';
import { startToDate } from '@/lib/courseStart';

export type MarathonFormData = {
  title: string;
  slug: string;
  cover_url?: string;
  background_url?: string;
  category?: string;
  language: Language;
  level_start: CEFRLevel;
  level_end: CEFRLevel;
  access_mode: AccessMode;
  show_rating: boolean;
  rating_scope: RatingScope;
  show_student_rank: boolean;
  is_private: boolean;
  /**
   * «Открытый для всех» (техдолг 321, 27.09.2026): курс виден каждому вошедшему без записи. Ключ — `is_free`, как в
   * ответах админке; сервер принимает его при правке курса только если поле прислано (applyMarathonPatch).
   */
  is_free: boolean;
  /** «Порядок отображения» в каталоге: меньше — выше; при равных — новее выше (`display_order ASC, created_at DESC`). */
  display_order: number;
  /** Дата старта обучения. Пусто — обучение уже идёт. */
  starts_at: string;
};

/**
 * Значения формы курса из курса (правка) или по умолчанию (новый курс).
 *
 * ⚠️ ОДНО МЕСТО НА ОТКРЫТИЕ И НА «ПОСЛЕ СОХРАНЕНИЯ» (разбор удобства 28.09, №7). Раньше форма брала значения только
 * при первом монтировании, а после сохранения звала `reset()` без аргументов — то есть возвращалась к значениям ДО
 * правки. Окно не пересоздаётся (`key={m.id}`), и второе открытие со «Сохранить» молча откатывало первую правку:
 * сервер применяет каждое присланное поле. Теперь форма после сохранения встаёт на ответ сервера, а при каждом
 * открытии — на текущий курс.
 */
export function marathonFormValues(initial?: Marathon | null): MarathonFormData {
  if (initial) {
    return {
      title: initial.title,
      slug: initial.slug,
      cover_url: initial.cover_url,
      background_url: initial.background_url,
      category: initial.category,
      // Бэкенд может вернуть язык в нижнем регистре ("en") — приводим к
      // значению из списка опций, иначе селект сбросится на первый пункт.
      language: (initial.language?.toUpperCase() as Language) || 'EN',
      level_start: initial.level_start || 'A1',
      level_end: initial.level_end || 'B2',
      access_mode: initial.access_mode,
      show_rating: initial.show_rating,
      rating_scope: initial.rating_scope ?? 'all',
      show_student_rank: initial.show_student_rank ?? false,
      is_private: initial.is_private ?? false,
      is_free: initial.is_free ?? false,
      display_order: initial.display_order ?? 0,
      // <input type="date"> понимает только ГГГГ-ММ-ДД; день считаем по
      // Казахстану, а не по поясу браузера администратора.
      starts_at: startToDate(initial.starts_at),
    } as MarathonFormData;
  }
  return {
    language: 'EN',
    level_start: 'A1',
    level_end: 'B2',
    access_mode: 'sequential',
    show_rating: true,
    rating_scope: 'all',
    show_student_rank: false,
    // Новые марафоны создаём приватными по умолчанию — доступ только через
    // ручное зачисление, пока админ явно не откроет публичный доступ.
    is_private: true,
    // Новый курс — закрытый: открыть для всех — отдельным решением, галочкой ниже.
    is_free: false,
    display_order: 0,
    starts_at: '',
  } as MarathonFormData;
}
